import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import JobCodeSection from '../components/JobCodeSection';
import Modal from '../components/Modal';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import AttachmentVoidModal from '../components/AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { splitVoided } from '../api/attachmentVoid';
import { useAuth } from '../context/AuthContext';
import { formatDateTimePT } from '../utils/timezone';
import { dateTimeLocalToIso, isoToDateTimeLocal } from '../utils/dateTimeInput';
import {
  addOnsiteServiceNote,
  addOnsiteServiceTask,
  completeOnsiteServiceAppointment,
  deleteOnsiteServiceTask,
  fetchOnsiteServiceAppointment,
  fetchOnsiteServiceAttachments,
  fetchOnsiteServiceNotes,
  onsiteServiceAttachmentDownloadUrl,
  reopenOnsiteServiceAppointment,
  unvoidOnsiteServiceAttachment,
  updateOnsiteServiceAppointment,
  updateOnsiteServiceTask,
  uploadOnsiteServiceAttachment,
  voidOnsiteServiceAppointment,
  voidOnsiteServiceAttachment,
  type OnsiteServiceAttachmentRow,
  type OnsiteServiceNoteRow,
  type OnsiteServiceRow,
} from '../api/onsiteServiceAppointments';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import './Page.css';
import './Elevators.css';
import './Installation.css';
import './OnsiteServiceDetail.css';

function fileSizeLabel(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const OnsiteServiceDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appt, setAppt] = useState<OnsiteServiceRow | null>(null);
  const [notes, setNotes] = useState<OnsiteServiceNoteRow[]>([]);
  const [attachments, setAttachments] = useState<OnsiteServiceAttachmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const [newTask, setNewTask] = useState('');
  const [noteDraft, setNoteDraft] = useState('');

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editBusiness, setEditBusiness] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPoc, setEditPoc] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editMultiDay, setEditMultiDay] = useState(false);
  const [editAssignees, setEditAssignees] = useState<number[]>([]);
  const [editError, setEditError] = useState('');
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);

  const readOnly = appt != null && appt.status !== 'Scheduled';

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id < 1) {
      setLoadError('Invalid appointment id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [row, noteRows, attachmentRows] = await Promise.all([
        fetchOnsiteServiceAppointment(id),
        fetchOnsiteServiceNotes(id),
        fetchOnsiteServiceAttachments(id),
      ]);
      setAppt(row);
      setNotes(noteRows);
      setAttachments(attachmentRows);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load appointment.');
      setAppt(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list);
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const assigneePickerUsers = useMemo(() => {
    const hasRole = (u: VerifiedUser) => (u.roles ?? []).includes('installation');
    const ordered = [...verifiedUsers].sort((a, b) => {
      const ai = hasRole(a) ? 0 : 1;
      const bi = hasRole(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      return a.username.localeCompare(b.username);
    });
    return ordered.map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const visitWindow = useMemo(() => {
    if (!appt) return '—';
    const opts: Intl.DateTimeFormatOptions = { month: 'short', second: undefined };
    const start = formatDateTimePT(appt.service_at, opts);
    if (!appt.service_end_at) return start;
    return `${start} – ${formatDateTimePT(appt.service_end_at, opts)}`;
  }, [appt]);

  const withBusy = async (fn: () => Promise<void>) => {
    setActionBusy(true);
    setActionError('');
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleComplete = () =>
    withBusy(async () => {
      const updated = await completeOnsiteServiceAppointment(id);
      setAppt(updated);
    });

  const handleReopen = () =>
    withBusy(async () => {
      const updated = await reopenOnsiteServiceAppointment(id);
      setAppt(updated);
    });

  const handleVoid = () =>
    withBusy(async () => {
      const updated = await voidOnsiteServiceAppointment(id, voidReason.trim());
      setAppt(updated);
      setVoidOpen(false);
      setVoidReason('');
    });

  const handleAddTask = () =>
    withBusy(async () => {
      const description = newTask.trim();
      if (!description) return;
      const task = await addOnsiteServiceTask(id, description);
      setAppt((prev) => (prev ? { ...prev, tasks: [...prev.tasks, task] } : prev));
      setNewTask('');
    });

  const handleToggleTask = (taskId: number, isComplete: boolean) =>
    withBusy(async () => {
      const task = await updateOnsiteServiceTask(id, taskId, { is_complete: isComplete });
      setAppt((prev) =>
        prev ? { ...prev, tasks: prev.tasks.map((t) => (t.id === task.id ? task : t)) } : prev
      );
    });

  const handleRemoveTask = (taskId: number) =>
    withBusy(async () => {
      await deleteOnsiteServiceTask(id, taskId);
      setAppt((prev) => (prev ? { ...prev, tasks: prev.tasks.filter((t) => t.id !== taskId) } : prev));
    });

  const handleAddNote = () =>
    withBusy(async () => {
      if (!user?.id) return;
      const text = noteDraft.trim();
      if (!text) return;
      const note = await addOnsiteServiceNote(id, text, user.id);
      setNotes((prev) => [...prev, note]);
      setNoteDraft('');
    });

  const handleUpload = (file: File) =>
    withBusy(async () => {
      const row = await uploadOnsiteServiceAttachment(id, file, user?.id ?? null);
      setAttachments((prev) => [...prev, row]);
    });

  const replaceAttachment = (row: OnsiteServiceAttachmentRow) =>
    setAttachments((prev) => prev.map((a) => (a.id === row.id ? { ...a, ...row } : a)));

  const attachmentVoid = useAttachmentVoid<OnsiteServiceAttachmentRow>({
    label: (a) => a.original_file_name,
    onVoid: async (a, reason) => {
      replaceAttachment(await voidOnsiteServiceAttachment(id, a.id, reason, user?.id ?? null));
    },
    onUnvoid: async (a) => {
      replaceAttachment(await unvoidOnsiteServiceAttachment(id, a.id, user?.id ?? null));
    },
    onError: setActionError,
  });

  const { active: liveAttachments, voided: voidedAttachments } = splitVoided(attachments);

  /** One file, live or voided. A voided file stays downloadable — it is the record. */
  const renderAttachment = (a: OnsiteServiceAttachmentRow) => {
    const voided = a.status === 'Void';
    return (
      <li className={`onsite-attachment-item${voided ? ' attachment-voided' : ''}`} key={a.id}>
        <a
          href={onsiteServiceAttachmentDownloadUrl(id, a.id)}
          target="_blank"
          rel="noreferrer"
          className="onsite-attachment-link attachment-void-name"
        >
          {a.original_file_name}
        </a>
        {voided ? <AttachmentVoidPill /> : null}
        <span className="onsite-attachment-meta">
          {[fileSizeLabel(a.file_size), a.uploaded_by].filter(Boolean).join(' · ')}
        </span>
        {!readOnly ? (
          <button
            type="button"
            className={voided ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
            disabled={actionBusy || attachmentVoid.busy}
            onClick={() => (voided ? attachmentVoid.unvoid(a) : attachmentVoid.requestVoid(a))}
          >
            {voided ? 'Unvoid' : 'Void'}
          </button>
        ) : null}
        <AttachmentVoidReason attachment={a} />
      </li>
    );
  };

  const openEdit = () => {
    if (!appt) return;
    setEditBusiness(appt.business_name);
    setEditAddress(appt.business_address ?? '');
    setEditPoc(appt.point_of_contact ?? '');
    setEditPhone(appt.poc_phone ?? '');
    setEditEmail(appt.poc_email ?? '');
    setEditReason(appt.reason ?? '');
    setEditStart(isoToDateTimeLocal(appt.service_at));
    setEditEnd(isoToDateTimeLocal(appt.service_end_at));
    setEditMultiDay(appt.service_end_at != null);
    setEditAssignees(appt.assignees.map((a) => a.user_id));
    setEditError('');
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editBusiness.trim()) {
      setEditError('Business name is required.');
      return;
    }
    if (!editReason.trim()) {
      setEditError('A reason for the visit is required.');
      return;
    }
    if (editMultiDay && !editEnd) {
      setEditError('Enter when the visit ends, or switch back to a single day.');
      return;
    }
    setActionBusy(true);
    setEditError('');
    try {
      const updated = await updateOnsiteServiceAppointment(id, {
        business_name: editBusiness.trim(),
        business_address: editAddress.trim() || null,
        point_of_contact: editPoc.trim() || null,
        poc_phone: editPhone.trim() || null,
        poc_email: editEmail.trim() || null,
        reason: editReason.trim(),
        service_at: dateTimeLocalToIso(editStart),
        service_end_at: editMultiDay ? dateTimeLocalToIso(editEnd) : null,
        assignee_user_ids: editAssignees,
      });
      setAppt(updated);
      setEditOpen(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setActionBusy(false);
    }
  };

  const doneCount = appt?.tasks.filter((t) => t.is_complete).length ?? 0;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Onsite Service" subtitle="Technician visit details, tasks, notes and attachments" />
          <div className="page-content">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/installation?tab=service-support&section=onsite-service')}
              style={{ marginBottom: '1rem' }}
            >
              ← Back to Onsite Service
            </button>

            {loading ? (
              <p className="page-subtitle">Loading appointment…</p>
            ) : loadError ? (
              <p className="create-order-error" role="alert">
                {loadError}
              </p>
            ) : appt ? (
              <>
                <div className="si-detail-header">
                  <div>
                    <h2 style={{ margin: '0 0 0.35rem' }}>{appt.business_name}</h2>
                    <span className={`si-status-badge si-status-${appt.status}`}>{appt.status}</span>
                  </div>
                  <div className="si-detail-actions">
                    {appt.status === 'Scheduled' ? (
                      <>
                        <button
                          type="button"
                          className="update-button"
                          disabled={actionBusy}
                          onClick={() => void handleComplete()}
                        >
                          Complete
                        </button>
                        <button type="button" className="elevator-btn" disabled={actionBusy} onClick={openEdit}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cancel-button"
                          disabled={actionBusy}
                          onClick={() => setVoidOpen(true)}
                        >
                          Void
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="cancel-button"
                        disabled={actionBusy}
                        onClick={() => void handleReopen()}
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>

                <JobCodeSection jobCode={appt.job_code} clientId={appt.client_id} />

                <div className="si-info-card">
                  <div className="si-info-item">
                    <span className="si-info-label">Business Address</span>
                    <span className="si-info-value">{appt.business_address || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Point of Contact</span>
                    <span className="si-info-value">{appt.point_of_contact || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Contact Phone</span>
                    <span className="si-info-value">{appt.poc_phone || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Contact Email</span>
                    <span className="si-info-value">{appt.poc_email || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Visit</span>
                    <span className="si-info-value">{visitWindow}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Technicians</span>
                    <span className="si-info-value">
                      {appt.assignees.length ? appt.assignees.map((a) => a.username).join(', ') : '—'}
                    </span>
                  </div>
                  <div className="si-info-item onsite-info-item--wide">
                    <span className="si-info-label">Reason for the visit</span>
                    <span className="si-info-value">{appt.reason || '—'}</span>
                  </div>
                </div>

                {appt.status === 'Void' && appt.void_reason ? (
                  <p className="create-order-error">Voided: {appt.void_reason}</p>
                ) : null}

                {readOnly ? (
                  <p className="elevators-confirm-hint">
                    This appointment is {appt.status.toLowerCase()} and is read-only. Reopen it to make changes.
                  </p>
                ) : null}

                {actionError ? (
                  <p className="create-order-error" role="alert">
                    {actionError}
                  </p>
                ) : null}

                <section className="si-form-section">
                  <h3 className="si-form-section-title">
                    Tasks{' '}
                    <span className="events-section-count">
                      ({doneCount}/{appt.tasks.length} done)
                    </span>
                  </h3>
                  {appt.tasks.length === 0 ? (
                    <p className="page-subtitle">No tasks listed for this visit.</p>
                  ) : (
                    <ul className="onsite-task-list">
                      {appt.tasks.map((task) => (
                        <li className="onsite-task-item" key={task.id}>
                          <label className="onsite-task-label">
                            <input
                              type="checkbox"
                              checked={task.is_complete}
                              disabled={readOnly || actionBusy}
                              onChange={(e) => void handleToggleTask(task.id, e.target.checked)}
                            />
                            <span className={task.is_complete ? 'onsite-task-done' : undefined}>
                              {task.description}
                            </span>
                          </label>
                          {!readOnly ? (
                            <button
                              type="button"
                              className="onsite-task-remove"
                              disabled={actionBusy}
                              onClick={() => void handleRemoveTask(task.id)}
                              aria-label={`Remove task: ${task.description}`}
                            >
                              ×
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!readOnly ? (
                    <div className="onsite-task-input-row">
                      <input
                        type="text"
                        className="form-input"
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="Add another task…"
                        disabled={actionBusy}
                        aria-label="New task"
                      />
                      <button
                        type="button"
                        className="elevator-btn"
                        disabled={actionBusy || !newTask.trim()}
                        onClick={() => void handleAddTask()}
                      >
                        Add task
                      </button>
                    </div>
                  ) : null}
                </section>

                <section className="si-form-section">
                  <h3 className="si-form-section-title">
                    Notes <span className="events-section-count">({notes.length})</span>
                  </h3>
                  {notes.length === 0 ? (
                    <p className="page-subtitle">No notes yet.</p>
                  ) : (
                    <ul className="onsite-note-list">
                      {notes.map((note) => (
                        <li className="onsite-note-item" key={note.id}>
                          <div className="onsite-note-meta">
                            <strong>{note.submitted_by || 'Someone'}</strong>
                            <span>{formatDateTimePT(note.created_at, { second: undefined })}</span>
                          </div>
                          <p className="onsite-note-body">{note.note}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!readOnly ? (
                    <div className="form-group">
                      <label htmlFor="onsite-note-draft" className="form-label">
                        Add a note
                      </label>
                      <textarea
                        id="onsite-note-draft"
                        className="form-textarea"
                        rows={3}
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder="What happened on site?"
                        disabled={actionBusy}
                      />
                      <button
                        type="button"
                        className="update-button"
                        disabled={actionBusy || !noteDraft.trim()}
                        onClick={() => void handleAddNote()}
                      >
                        {actionBusy ? 'Saving…' : 'Add note'}
                      </button>
                    </div>
                  ) : null}
                </section>

                <section className="si-form-section">
                  <h3 className="si-form-section-title">
                    Attachments <span className="events-section-count">({liveAttachments.length})</span>
                  </h3>
                  {!readOnly ? (
                    <input
                      type="file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleUpload(f);
                        e.target.value = '';
                      }}
                      disabled={actionBusy}
                      aria-label="Upload an attachment"
                    />
                  ) : null}
                  {liveAttachments.length === 0 ? (
                    <p className="page-subtitle">No attachments yet.</p>
                  ) : (
                    <ul className="onsite-attachment-list">
                      {liveAttachments.map((a) => renderAttachment(a))}
                    </ul>
                  )}
                  {voidedAttachments.length > 0 ? (
                    <div className="attachment-voided-group">
                      <p className="attachment-voided-group-title">Voided ({voidedAttachments.length})</p>
                      <ul className="onsite-attachment-list">
                        {voidedAttachments.map((a) => renderAttachment(a))}
                      </ul>
                    </div>
                  ) : null}
                </section>
              </>
            ) : null}
          </div>
        </main>
      </div>

      <Modal isOpen={voidOpen} onClose={() => setVoidOpen(false)} title="Void appointment">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="onsite-void-reason">
              Reason
            </label>
            <textarea
              id="onsite-void-reason"
              className="form-input"
              rows={4}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Why is this appointment being voided?"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setVoidOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            disabled={actionBusy || !voidReason.trim()}
            onClick={() => void handleVoid()}
          >
            {actionBusy ? 'Voiding…' : 'Void appointment'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit appointment" wide>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="onsite-edit-business" className="form-label">
              Business Name
            </label>
            <input
              id="onsite-edit-business"
              type="text"
              className="form-input"
              value={editBusiness}
              onChange={(e) => setEditBusiness(e.target.value)}
              disabled={actionBusy}
            />
          </div>
          <div className="form-group">
            <label htmlFor="onsite-edit-address" className="form-label">
              Business Address
            </label>
            <input
              id="onsite-edit-address"
              type="text"
              className="form-input"
              value={editAddress}
              onChange={(e) => setEditAddress(e.target.value)}
              disabled={actionBusy}
            />
          </div>
          <div className="elevators-form-row">
            <div className="form-group">
              <label htmlFor="onsite-edit-poc" className="form-label">
                Point of Contact
              </label>
              <input
                id="onsite-edit-poc"
                type="text"
                className="form-input"
                value={editPoc}
                onChange={(e) => setEditPoc(e.target.value)}
                disabled={actionBusy}
              />
            </div>
            <div className="form-group">
              <label htmlFor="onsite-edit-phone" className="form-label">
                Phone Number
              </label>
              <input
                id="onsite-edit-phone"
                type="tel"
                className="form-input"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                disabled={actionBusy}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="onsite-edit-email" className="form-label">
              Email Address
            </label>
            <input
              id="onsite-edit-email"
              type="email"
              className="form-input"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              disabled={actionBusy}
            />
          </div>
          <div className="form-group">
            <label htmlFor="onsite-edit-reason" className="form-label">
              Reason for the visit
            </label>
            <textarea
              id="onsite-edit-reason"
              className="form-textarea"
              rows={2}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              disabled={actionBusy}
            />
          </div>
          <div className="form-group">
            <label htmlFor="onsite-edit-start" className="form-label">
              {editMultiDay ? 'Visit starts' : 'Date & Time of Visit'}
            </label>
            <input
              id="onsite-edit-start"
              type="datetime-local"
              className="form-input"
              value={editStart}
              onChange={(e) => setEditStart(e.target.value)}
              disabled={actionBusy}
            />
          </div>
          <div className="form-group">
            <label className="elevators-radio-label">
              <input
                type="checkbox"
                checked={editMultiDay}
                onChange={(e) => {
                  setEditMultiDay(e.target.checked);
                  if (!e.target.checked) setEditEnd('');
                }}
                disabled={actionBusy}
              />
              This visit spans a date range
            </label>
          </div>
          {editMultiDay ? (
            <div className="form-group">
              <label htmlFor="onsite-edit-end" className="form-label">
                Visit ends
              </label>
              <input
                id="onsite-edit-end"
                type="datetime-local"
                className="form-input"
                value={editEnd}
                onChange={(e) => setEditEnd(e.target.value)}
                disabled={actionBusy}
              />
            </div>
          ) : null}
          <div className="form-group">
            <span className="form-label">Technician(s)</span>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={editAssignees}
              onChange={setEditAssignees}
              addPlaceholder="Search employee to assign…"
            />
          </div>
          {editError ? (
            <p className="create-order-error" role="alert">
              {editError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setEditOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={actionBusy} onClick={() => void submitEdit()}>
            {actionBusy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Modal>

      <AttachmentVoidModal control={attachmentVoid} />
    </div>
  );
};

export default OnsiteServiceDetail;
